import { ReactElement, ReactNode, useEffect, useMemo } from "react";
import type { NextPage } from "next";
import Head from "next/head";
import type { AppProps } from "next/app";

import MetaViewport from "shared/ui/meta-viewport";

import { useDatadogRum } from "@/helpers/datadog";
import { GoogleTagManager } from "@/helpers/tracking";
import { openAccountDetails } from "@/helpers/account-details";

// FONTs import
// https://fontsource.org/docs/guides/nextjs

// Changed In favour of google fonts
// import "@fontsource/inter/100.css";
// import "@fontsource/inter/200.css";
// import "@fontsource/inter/300.css";
// import "@fontsource/inter/400.css";
// import "@fontsource/inter/500.css";
// import "@fontsource/inter/600.css";
// import "@fontsource/inter/700.css";
// import "@fontsource/inter/800.css";
// import "@fontsource/inter/900.css";

// import HomepageLayout from "@/components/layouts/homepage-layout";
import AppLayout from "@/components/layouts/app";
import { AppLayoutPagePropsI } from "@/lib/types/layout";

import { useRouter } from "next/router";
import {
  APP_LAYOUT_SUPPORTED_ROUTES,
  RESPONSIVE_PAGE_ROUTES,
} from "@/constants/layout";

import "./globals.css";

export type NextPageWithLayout<P = any, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps<AppLayoutPagePropsI> & {
  Component: NextPageWithLayout;
};

export default function App({ Component, pageProps }: AppPropsWithLayout) {
  const { route } = useRouter();

  useDatadogRum();

  // E2E test harness: in non-production builds expose imperative helpers so
  // Playwright can mount drawers/modals via page.evaluate() without driving
  // a full UI navigation chain. Guarded against production to keep the
  // shipped bundle clean.
  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      process.env.NODE_ENV !== "production"
    ) {
      (window as unknown as { __e2e?: Record<string, unknown> }).__e2e = {
        openAccountDetails,
      };
    }
  }, []);

  // Use the layout defined at the page level, if available
  // const getLayout = Component.getLayout ?? ((page) => page);

  const pageInternals = useMemo(
    () => (
      <>
        <MetaViewport
          head={Head}
          responsivePageRoutes={RESPONSIVE_PAGE_ROUTES}
        />

        <GoogleTagManager />

        <Component {...pageProps} />
        {/**
         * NOTE: with a SPA approach the loader regression has been introduced many times
         *       is not able to indicate that loading has been completed
         */}
        {/* <NavigationProgressbar /> */}
      </>
    ),
    [Component, pageProps]
  );

  // E2E harness routes (/__e2e/*) need the full AppLayout so query/api
  // providers are present, but they are not authoritative app routes
  // listed in APP_LAYOUT_SUPPORTED_ROUTES.
  const isE2EHarness =
    route.startsWith("/__e2e") && process.env.NODE_ENV !== "production";

  if (!APP_LAYOUT_SUPPORTED_ROUTES.includes(route) && !isE2EHarness) {
    return pageInternals;
  }

  return <AppLayout layoutProps={pageProps}>{pageInternals}</AppLayout>;
}
